import React from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  TextField,
  Alert,
  Divider,
  Stack
} from '@mui/material';
import { Form990Data } from '../../types/form990.types';
import { SignaturePad } from '../../../../common/SignaturePad';

interface SignaturePanelProps {
  data: Form990Data;
  onSign: (signatureData: {
    signature: string;
    title: string;
    date: string;
  }) => Promise<void>;
  onSubmit: () => Promise<void>;
}

export const SignaturePanel: React.FC<SignaturePanelProps> = ({
  data,
  onSign,
  onSubmit
}) => {
  const [signature, setSignature] = React.useState<string>('');
  const [title, setTitle] = React.useState('');
  const [date, setDate] = React.useState(new Date().toISOString().split('T')[0]);
  const [error, setError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSign = async () => {
    try {
      setIsSubmitting(true);
      if (!signature || !title) {
        setError('Signature and title are required');
        return;
      }
      await onSign({
        signature,
        title,
        date
      });
      await onSubmit();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    setSignature('');
    setTitle('');
    setDate(new Date().toISOString().split('T')[0]);
  };

  return (
    <Paper sx={{ p: 3, backgroundColor: 'transparent' }}>
      <Stack spacing={2}>
        <Typography 
          variant="h6" 
          sx={{ 
            color: 'rgba(255, 255, 255, 0.87)',
            fontWeight: 500 
          }}
        >
          Officer Signature
        </Typography>
        
        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: 'rgba(255, 255, 255, 0.87)'
          }}
        >
          Organization Information
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            color: '#0066FF',
            fontWeight: 400
          }}
        >
          {data?.data?.header?.name || 'Organization name not provided'}
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            color: '#0066FF',
            fontWeight: 400
          }}
        >
          EIN: {data?.data?.header?.ein || 'EIN not provided'}
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            color: '#0066FF',
            fontWeight: 400
          }}
        >
          Tax Year
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            color: '#0066FF',
            fontWeight: 400
          }}
        >
          {data?.year || 'Tax year not provided'}
        </Typography>

        <TextField
          label="Officer Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          fullWidth
          InputLabelProps={{
            sx: { color: 'rgba(255, 255, 255, 0.7)' }
          }}
          InputProps={{
            sx: { color: 'white' }
          }}
        />

        <TextField
          label="Date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          required
          fullWidth
          InputLabelProps={{
            sx: { color: 'rgba(255, 255, 255, 0.7)' }
          }}
          InputProps={{
            sx: { color: 'white' }
          }}
        />

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Signature
          </Typography>
          <Box sx={{ 
            width: '100%',
            height: '300px',
            border: '1px solid rgba(0, 0, 0, 0.23)',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <SignaturePad
              onChange={setSignature}
              height={250}
              width={400}
            />
          </Box>
        </Box>

        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            onClick={handleClear}
            disabled={isSubmitting}
            fullWidth
            sx={{
              height: 48,
              color: '#0066FF',
              borderColor: '#0066FF',
              backgroundColor: 'transparent',
              '&:hover': {
                borderColor: '#0066FF',
                backgroundColor: 'rgba(0, 102, 255, 0.08)',
              },
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500
            }}
          >
            Clear All
          </Button>
          <Button
            variant="contained"
            onClick={handleSign}
            disabled={!signature || !title || isSubmitting}
            fullWidth
            sx={(theme) => ({
              height: 48,
              backgroundColor: '#0066FF !important',
              boxShadow: theme.shadows[1],
              '&:hover': {
                backgroundColor: '#0052CC !important',
                boxShadow: theme.shadows[2],
              },
              '&:active': {
                backgroundColor: '#0047B3 !important',
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.12) !important',
                color: 'rgba(255, 255, 255, 0.3)'
              },
              textTransform: 'uppercase',
              fontSize: '0.875rem',
              fontWeight: 500,
              letterSpacing: '0.02857em',
              lineHeight: 1.75
            })}
          >
            {isSubmitting ? 'Submitting...' : 'Sign and Submit Form 990'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}; 